import { ArrowLeft, Send, Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { Textarea } from '@/components/ui/textarea'
import { errorMessage, isApiError } from '@/lib/api/errors'
import { formatDate } from '@/lib/format'
import {
  type EmailAutomation,
  type EmailInput,
  type EmailList,
  useDeleteEmail,
  useEmail,
  useEmailPreview,
  useEmails,
  useSaveEmail,
  useSendTestEmail,
} from './api'

type Unit = 'minutes' | 'hours' | 'days'
const unitMinutes: Record<Unit, number> = { minutes: 1, hours: 60, days: 1440 }

/** The largest unit the wait divides into evenly. */
function splitWait(minutes: number): { amount: number; unit: Unit } {
  if (minutes > 0 && minutes % 1440 === 0) return { amount: minutes / 1440, unit: 'days' }
  if (minutes > 0 && minutes % 60 === 0) return { amount: minutes / 60, unit: 'hours' }
  return { amount: minutes, unit: 'minutes' }
}

const blank: EmailInput = {
  name: '',
  trigger: 'account.created',
  delay_minutes: 0,
  condition: null,
  subject: '',
  preheader: null,
  body: 'Hi {{first_name}},\n\n',
  button_label: null,
  button_url: null,
}

/** Write or edit a lifecycle email: /emails/new or /emails/:emailId. */
export function EmailEditorPage() {
  const { emailId } = useParams()
  const email = useEmail(emailId)
  const list = useEmails()

  if ((emailId !== undefined && email.isPending) || list.isPending) return <Skeleton className="h-96" />
  if (email.isError) return <ErrorState error={email.error} onRetry={() => void email.refetch()} />
  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />

  return <Editor key={email.data?.id ?? 'new'} email={email.data ?? null} meta={list.data.meta} />
}

function Editor({ email, meta }: { email: EmailAutomation | null; meta: EmailList['meta'] }) {
  const navigate = useNavigate()
  const save = useSaveEmail(email?.id)
  const remove = useDeleteEmail()
  const sendTest = useSendTestEmail()
  const body = useRef<HTMLTextAreaElement>(null)
  const cursorSet = useRef(false)
  const [values, setValues] = useState<EmailInput>(email ?? blank)
  const [wait, setWait] = useState(() => splitWait(email?.delay_minutes ?? 0))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [tab, setTab] = useState<'write' | 'preview'>('write')
  const [confirming, setConfirming] = useState(false)
  const canEdit = meta.can_edit
  const content = {
    subject: values.subject,
    preheader: values.preheader,
    body: values.body,
    button_label: values.button_label,
    button_url: values.button_url,
  }
  const preview = useEmailPreview(content, tab === 'preview')
  const set = <K extends keyof EmailInput>(key: K, value: EmailInput[K]) =>
    setValues((v) => ({ ...v, [key]: value }))

  // Merge tags go in where the cursor was in the message, or at the end.
  const insertTag = (tag: string) => {
    const el = body.current
    const text = `{{${tag}}}`
    if (!el || !cursorSet.current) {
      set('body', values.body + text)
      return
    }
    const start = el.selectionStart
    set('body', values.body.slice(0, start) + text + values.body.slice(el.selectionEnd))
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(start + text.length, start + text.length)
    })
  }

  const submit = (enabled?: boolean) => {
    setErrors({})
    save.mutate(
      {
        ...values,
        name: values.name.trim(),
        delay_minutes: Math.max(0, Math.round(wait.amount * unitMinutes[wait.unit])),
        preheader: values.preheader?.trim() || null,
        button_label: values.button_label?.trim() || null,
        button_url: values.button_url?.trim() || null,
        ...(enabled === undefined ? {} : { enabled }),
      },
      {
        onSuccess: (saved) => {
          toast.success(
            enabled === undefined
              ? 'Saved.'
              : saved.enabled
                ? `${saved.name} is on.`
                : `${saved.name} is off.`,
          )
          if (!email) void navigate(`/emails/${saved.id}`, { replace: true })
        },
        onError: (err) => {
          if (isApiError(err) && err.isValidation) {
            setErrors(Object.fromEntries(Object.entries(err.errors).map(([k, v]) => [k, v[0] ?? ''])))
          } else toast.error(errorMessage(err))
        },
      },
    )
  }

  const trigger = meta.triggers[values.trigger]

  return (
    <>
      <Link
        to="/emails"
        className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-ink-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> Emails
      </Link>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="grid gap-1">
          <h1 className="display text-2xl text-ink">{email ? email.name : 'New email'}</h1>
          {email && (
            <p className="flex flex-wrap items-center gap-2 text-[13px] text-ink-muted">
              <Badge tone={email.enabled ? 'success' : 'outline'}>{email.enabled ? 'On' : 'Off'}</Badge>
              {email.built_in && <Badge tone="neutral">Built in</Badge>}
              {email.updated_at && `Updated ${formatDate(email.updated_at)}`}
              {email.updated_by && ` by ${email.updated_by}`}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            loading={sendTest.isPending}
            disabled={!values.subject.trim() || !values.body.trim()}
            onClick={() =>
              sendTest.mutate(content, {
                onSuccess: (r) => toast.success(`Test sent to ${r.sent_to}.`),
                onError: (err) => toast.error(errorMessage(err)),
              })
            }
          >
            <Send /> Send me a test
          </Button>
          {email && canEdit && !email.built_in && (
            <Button variant="ghost" onClick={() => setConfirming(true)}>
              <Trash2 /> Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <Card className="grid gap-5 p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Subject" htmlFor="email-subject" required error={errors.subject}>
              <Input
                id="email-subject"
                disabled={!canEdit}
                value={values.subject}
                onChange={(e) => set('subject', e.target.value)}
                placeholder="Welcome to Arva SEO, {{first_name}}"
              />
            </Field>
            <Field
              label="Preview text"
              htmlFor="email-preheader"
              error={errors.preheader}
              hint="Shown after the subject in most inboxes."
            >
              <Input
                id="email-preheader"
                disabled={!canEdit}
                value={values.preheader ?? ''}
                onChange={(e) => set('preheader', e.target.value)}
              />
            </Field>
          </div>

          <div className="grid gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <label
                htmlFor="email-body"
                className="text-[13px] font-medium text-ink after:ml-0.5 after:text-danger after:content-['*']"
              >
                Message
              </label>
              <Segmented<'write' | 'preview'>
                label="Editor view"
                value={tab}
                onChange={setTab}
                options={[
                  { value: 'write', label: 'Write' },
                  { value: 'preview', label: 'Preview' },
                ]}
              />
            </div>
            {tab === 'write' ? (
              <>
                <Textarea
                  id="email-body"
                  ref={body}
                  disabled={!canEdit}
                  className="min-h-[22rem] font-mono text-[13px] leading-relaxed"
                  value={values.body}
                  onChange={(e) => set('body', e.target.value)}
                  onSelect={() => {
                    cursorSet.current = true
                  }}
                  aria-invalid={errors.body ? true : undefined}
                />
                {errors.body && (
                  <p role="alert" className="text-xs text-danger">
                    {errors.body}
                  </p>
                )}
                {canEdit && (
                  <div className="flex flex-wrap items-center gap-1.5 pt-1">
                    <span className="text-xs text-ink-subtle">Insert:</span>
                    {Object.entries(meta.merge_tags).map(([tag, label]) => (
                      <button
                        key={tag}
                        type="button"
                        title={label}
                        onClick={() => insertTag(tag)}
                        className="rounded-full border border-border-strong px-2 py-0.5 font-mono text-[11px] text-ink-muted hover:border-ink hover:text-ink"
                      >
                        {`{{${tag}}}`}
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-xs text-ink-subtle">
                  Markdown: <code># Heading</code>, <code>**bold**</code>, <code>- list item</code>,{' '}
                  <code>[link](https://…)</code>. Put <code>{'{{button}}'}</code> on its own line to place the
                  button; otherwise it goes at the end.
                </p>
              </>
            ) : (
              <div className="grid gap-2">
                <p className="text-[13px] text-ink-muted">
                  Subject: <strong className="text-ink">{preview.data?.subject ?? values.subject}</strong>
                  <span className="block text-xs text-ink-subtle">With made-up details, and your name.</span>
                </p>
                <iframe
                  title="Email preview"
                  sandbox=""
                  srcDoc={preview.data?.html ?? ''}
                  className="h-[40rem] w-full rounded-md border border-border bg-white"
                />
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Button text"
              htmlFor="email-button-label"
              error={errors.button_label}
              hint="Optional."
            >
              <Input
                id="email-button-label"
                disabled={!canEdit}
                value={values.button_label ?? ''}
                onChange={(e) => set('button_label', e.target.value)}
                placeholder="Add your first website"
              />
            </Field>
            <Field
              label="Button link"
              htmlFor="email-button-url"
              error={errors.button_url}
              hint="A page in the app (/…), {{workspace_url}}/…, or https://…"
            >
              <Input
                id="email-button-url"
                disabled={!canEdit}
                value={values.button_url ?? ''}
                onChange={(e) => set('button_url', e.target.value)}
                placeholder="{{workspace_url}}/websites/new"
              />
            </Field>
          </div>
        </Card>

        <div className="grid h-fit gap-4">
          <Card className="grid gap-4 p-5">
            <Field label="Name" htmlFor="email-name" required error={errors.name} hint="Only staff see it.">
              <Input
                id="email-name"
                disabled={!canEdit}
                value={values.name}
                onChange={(e) => set('name', e.target.value)}
              />
            </Field>
            <Field label="When" htmlFor="email-trigger" error={errors.trigger} hint={trigger?.description}>
              <NativeSelect
                id="email-trigger"
                disabled={!canEdit}
                value={values.trigger}
                onChange={(e) => set('trigger', e.target.value)}
              >
                {Object.entries(meta.triggers).map(([key, t]) => (
                  <option key={key} value={key}>
                    {t.label}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field
              label="Wait"
              htmlFor="email-wait"
              error={errors.delay_minutes}
              hint="0 sends it straight away."
            >
              <div className="flex gap-2">
                <Input
                  id="email-wait"
                  type="number"
                  min={0}
                  disabled={!canEdit}
                  className="w-24"
                  value={wait.amount}
                  onChange={(e) => setWait((w) => ({ ...w, amount: Number(e.target.value) || 0 }))}
                />
                <NativeSelect
                  aria-label="Wait unit"
                  disabled={!canEdit}
                  value={wait.unit}
                  onChange={(e) => setWait((w) => ({ ...w, unit: e.target.value as Unit }))}
                >
                  <option value="minutes">minutes</option>
                  <option value="hours">hours</option>
                  <option value="days">days</option>
                </NativeSelect>
              </div>
            </Field>
            <Field
              label="Only if"
              htmlFor="email-condition"
              error={errors.condition}
              hint="Checked when it's due; if it no longer holds, it's skipped."
            >
              <NativeSelect
                id="email-condition"
                disabled={!canEdit}
                value={values.condition ?? ''}
                onChange={(e) => set('condition', e.target.value || null)}
              >
                <option value="">Always</option>
                {Object.entries(meta.conditions).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </Card>
          {canEdit && (
            <Card className="grid gap-2 p-5">
              <Button
                onClick={() => submit()}
                loading={save.isPending && save.variables.enabled === undefined}
              >
                {email ? 'Save changes' : 'Save (off)'}
              </Button>
              {email && (
                <Button
                  variant="secondary"
                  onClick={() => submit(!email.enabled)}
                  loading={save.isPending && save.variables.enabled !== undefined}
                >
                  {email.enabled ? 'Turn off' : 'Save and turn on'}
                </Button>
              )}
              <p className="text-xs text-ink-subtle">
                {email?.enabled
                  ? 'It’s on: changes apply to emails sent from now on.'
                  : 'New emails start off. Send yourself a test, then turn it on.'}
              </p>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent
          title="Delete this email?"
          description="It stops, and emails still waiting to go out are dropped. To pause it instead, turn it off."
          footer={
            <>
              <DialogClose asChild>
                <Button variant="ghost">Cancel</Button>
              </DialogClose>
              <Button
                variant="danger"
                loading={remove.isPending}
                onClick={() =>
                  email &&
                  remove.mutate(email.id, {
                    onSuccess: () => {
                      toast.success('Email deleted.')
                      void navigate('/emails', { replace: true })
                    },
                    onError: (err) => toast.error(errorMessage(err)),
                  })
                }
              >
                Delete
              </Button>
            </>
          }
        />
      </Dialog>
    </>
  )
}
